import { useEffect, useRef, useState } from "react";import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Briefcase,
  GraduationCap,
  Loader2,
  Rocket,
  Sparkles,
  Trophy,
  Zap,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import JobCard from "@/components/jobs/JobCard";
import JobFilters from "@/components/jobs/JobFilters";
import api, { formatApiErrorDetail } from "@/lib/api";

const BUCKETS = [
  {
    key: "apply_now",
    label: "Apply Now",
    blurb: "Strong matches you can move on this week.",
    icon: Zap,
    iconClass: "bg-sky-100 text-sky-700",
  },
  {
    key: "fresh_start",
    label: "Fresh Start Roles",
    blurb: "Different direction — a clean slate without burning bridges.",
    icon: Sparkles,
    iconClass: "bg-violet-100 text-violet-700",
  },
  {
    key: "earn_while_you_learn",
    label: "Earn While You Learn",
    blurb: "Apprenticeships, traineeships, on-the-job-learning roles.",
    icon: GraduationCap,
    iconClass: "bg-amber-100 text-amber-700",
  },
  {
    key: "retraining_backed",
    label: "Retraining Backed",
    blurb: "Adjacent to a pathway you're already retraining for.",
    icon: Briefcase,
    iconClass: "bg-emerald-100 text-emerald-700",
  },
  {
    key: "high_salary_potential",
    label: "High Salary Potential",
    blurb: "Top of the range reaches or exceeds your target salary.",
    icon: Trophy,
    iconClass: "bg-rose-100 text-rose-700",
  },
];

const APPRENTICE_KEYWORDS = [
  "apprentice",
  "apprenticeship",
  "trainee",
  "traineeship",
  "graduate program",
  "junior",
  "entry-level",
  "earn while you learn",
];

function tokens(s) {
  return new Set(
    String(s || "")
      .toLowerCase()
      .replace(/[\/-]/g, " ")
      .split(/\s+/)
      .filter((t) => t.length >= 3)
  );
}

function overlapCount(a, b) {
  let n = 0;
  for (const t of a) if (b.has(t)) n++;
  return n;
}

function classifyJob(job, ctx) {
  const titleAndDesc = `${job.title || ""} ${job.description || ""}`.toLowerCase();
  if (APPRENTICE_KEYWORDS.some((k) => titleAndDesc.includes(k))) {
    return "earn_while_you_learn";
  }
  // High salary potential
  if (ctx.target && job.salary_max != null && job.salary_max >= ctx.target) {
    return "high_salary_potential";
  }
  // Retraining backed: best overlap with transitional or long-term pathway
  const jt = tokens(job.title);
  let bestKey = null;
  let bestOverlap = 0;
  for (const [pKey, ptokens] of Object.entries(ctx.pathwayTokens)) {
    const n = overlapCount(jt, ptokens);
    if (n > bestOverlap) {
      bestOverlap = n;
      bestKey = pKey;
    }
  }
  if (bestKey && (bestKey === "transitional" || bestKey === "long_term_future_proof")) {
    if (bestOverlap >= 1) return "retraining_backed";
  }
  // Apply Now: high match score
  if ((job.match_score ?? 0) >= 60) return "apply_now";
  // Fresh Start: low overlap with current role and immediate pathway
  const currentTokens = ctx.currentRoleTokens;
  const immTokens = ctx.pathwayTokens.immediate_income || new Set();
  const overlapCurrent = overlapCount(jt, currentTokens);
  const overlapImmediate = overlapCount(jt, immTokens);
  if (overlapCurrent === 0 && overlapImmediate === 0) return "fresh_start";
  return "apply_now";
}

function bucketForCategoryFilter(job, ctx) {
  // Used only by role_category filter — returns the BEST pathway match key or null
  const jt = tokens(job.title);
  let bestKey = null;
  let best = 0;
  for (const [k, t] of Object.entries(ctx.pathwayTokens)) {
    const n = overlapCount(jt, t);
    if (n > best) {
      best = n;
      bestKey = k;
    }
  }
  return bestKey;
}

function computeCtx(assessment) {
  const pathways = assessment?.ai_recommendation?.pathways;
  return {
    currentRoleTokens: tokens(assessment?.current_situation?.last_role),
    pathwayTokens: {
      immediate_income: tokens(pathways?.immediate_income?.role_title),
      transitional: tokens(pathways?.transitional?.role_title),
      long_term_future_proof: tokens(pathways?.long_term_future_proof?.role_title),
    },
    target: assessment?.salary?.target_annual,
  };
}

export default function Jobs() {
  const [assessment, setAssessment] = useState(null);
  const [matches, setMatches] = useState([]); // all returned jobs (flattened, latest search)
  const [saved, setSaved] = useState([]);
  const [applications, setApplications] = useState([]);
  const [connectorStatus, setConnectorStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchError, setSearchError] = useState("");
  const [searching, setSearching] = useState(false);
  const [keywords, setKeywords] = useState("");
  const [activeBucket, setActiveBucket] = useState("apply_now");
  const [filters, setFilters] = useState({
    location: "",
    salary_min: "",
    remote_type: "",
    role_category: "",
    source: "",
  });
  const [busyRows, setBusyRows] = useState({}); // job_id -> {save?, apply?}
  const initialKeywordsSet = useRef(false);

  // load assessment + saved + applications, then auto-run search (once on mount)
  // The deps are intentionally `[]` — this is a one-time bootstrap effect.
  // Subsequent searches are triggered by user actions via runSearch().
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [asmtRes, savedRes, appRes] = await Promise.all([
          api.get("/assessment/me"),
          api.get("/jobs/saved"),
          api.get("/jobs/applications"),
        ]);
        if (!active) return;
        setAssessment(asmtRes.data);
        setSaved(savedRes.data);
        setApplications(appRes.data);

        // Initial keywords = first pathway role_title OR last_role
        const ai = asmtRes.data?.ai_recommendation;
        const inferred =
          ai?.pathways?.immediate_income?.role_title ||
          ai?.pathways?.transitional?.role_title ||
          asmtRes.data?.current_situation?.last_role ||
          "";
        if (!initialKeywordsSet.current) {
          setKeywords(inferred);
          initialKeywordsSet.current = true;
          await runSearch(inferred);
        }
      } catch (err) {
        if (active) setSearchError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const runSearch = async (kw) => {
    setSearching(true);
    setSearchError("");
    try {
      const body = { keywords: kw || keywords || "", limit_per_source: 10 };
      const { data } = await api.post("/jobs/search", body);
      setMatches(data.jobs || []);
      setConnectorStatus(data.connector_status || {});
    } catch (err) {
      setSearchError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSearching(false);
    }
  };

  const ctx = computeCtx(assessment);

  const filteredJobs = matches.filter((j) => {
    if (filters.location) {
      const needle = filters.location.toLowerCase();
      const hay = `${j.location || ""} ${j.remote_type || ""}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    if (filters.salary_min && filters.salary_min !== "") {
      const cap = j.salary_max ?? j.salary_min ?? 0;
      if ((cap || 0) < Number(filters.salary_min)) return false;
    }
    if (filters.remote_type && j.remote_type !== filters.remote_type) return false;
    if (filters.source && j.source !== filters.source) return false;
    if (filters.role_category) {
      const best = bucketForCategoryFilter(j, ctx);
      if (best !== filters.role_category) return false;
    }
    return true;
  });

  const buckets = BUCKETS.reduce(
    (acc, b) => ({
      ...acc,
      [b.key]: filteredJobs.filter((j) => classifyJob(j, ctx) === b.key),
    }),
    {}
  );

  const savedKeys = new Set(
    saved.map((e) => {
      const j = e.job || {};
      return `${j.source}:${j.source_job_id || `${j.title}|${j.employer || ""}`}`;
    })
  );
  const appliedKeys = new Set(
    applications
      .filter((e) =>
        ["applied", "phone_screen", "interview", "offer", "hired"].includes(e.status)
      )
      .map((e) => {
        const j = e.job || {};
        return `${j.source}:${j.source_job_id || `${j.title}|${j.employer || ""}`}`;
      })
  );

  const keyFor = (j) => `${j.source}:${j.source_job_id || `${j.title}|${j.employer || ""}`}`;

  const onSave = async (job) => {
    const k = keyFor(job);
    if (savedKeys.has(k)) return;
    setBusyRows((b) => ({ ...b, [k]: { ...(b[k] || {}), save: "loading" } }));
    try {
      const { data } = await api.post("/jobs/saved", { job });
      setSaved((arr) => [data, ...arr]);
    } catch (err) {
      setSearchError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyRows((b) => ({ ...b, [k]: { ...(b[k] || {}), save: "idle" } }));
    }
  };

  const onMarkApplied = async (job) => {
    const k = keyFor(job);
    if (appliedKeys.has(k)) return;
    setBusyRows((b) => ({ ...b, [k]: { ...(b[k] || {}), apply: "loading" } }));
    try {
      const { data } = await api.post("/jobs/applications", { job, status: "applied" });
      setApplications((arr) => [data, ...arr]);
    } catch (err) {
      setSearchError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyRows((b) => ({ ...b, [k]: { ...(b[k] || {}), apply: "idle" } }));
    }
  };

  const onClearFilters = () =>
    setFilters({ location: "", salary_min: "", remote_type: "", role_category: "", source: "" });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="jobs-page">
        <Navbar />
        <main className="pt-24 lg:pt-28 pb-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center gap-3 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading job matches…
          </div>
        </main>
      </div>
    );
  }

  const active = BUCKETS.find((b) => b.key === activeBucket) || BUCKETS[0];
  const activeJobs = buckets[active.key] || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="jobs-page">
      <Navbar />
      <main className="pt-24 lg:pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Link
            to="/dashboard"
            className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1.5"
            data-testid="jobs-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to dashboard
          </Link>

          <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm uppercase tracking-[0.18em] text-sky-700 font-medium">
                Job matches
              </p>
              <h1
                className="mt-1.5 text-3xl lg:text-4xl font-medium tracking-tight text-slate-900"
                data-testid="jobs-heading"
              >
                Roles sorted by how well they fit you.
              </h1>
              <p className="mt-2 text-base text-slate-600 max-w-2xl">
                Scored against your assessment, then bucketed so you can pick a lane.
                Filters narrow the list; tabs change the lane.
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") runSearch(keywords);
                  }}
                  placeholder="Search keywords"
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  data-testid="jobs-keyword-input"
                />
                <button
                  type="button"
                  onClick={() => runSearch(keywords)}
                  disabled={searching}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
                  data-testid="jobs-search-button"
                >
                  {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Search
                </button>
              </div>
              <p className="text-[11px] text-slate-500">
                {`Searched ${Object.keys(connectorStatus).length} sources`}
              </p>
            </div>
          </div>

          {searchError ? (
            <div
              className="mt-4 rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700"
              data-testid="jobs-search-error"
            >
              {searchError}
            </div>
          ) : null}

          {/* Connector status banner */}
          {Object.keys(connectorStatus).length > 0 ? (
            <div
              className="mt-4 rounded-xl bg-white border border-slate-200 p-3 text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1"
              data-testid="jobs-connector-status"
            >
              {Object.entries(connectorStatus).map(([name, st]) => (
                <span
                  key={name}
                  className="inline-flex items-center gap-1.5"
                  data-testid={`jobs-connector-status-${name}`}
                >
                  <span
                    className={
                      "inline-block h-1.5 w-1.5 rounded-full " +
                      (st.status === "ok" || st.status === "demo"
                        ? "bg-emerald-500"
                        : st.status === "needs_credentials"
                        ? "bg-amber-500"
                        : "bg-slate-300")
                    }
                  />
                  <span className="font-medium text-slate-700">{name}</span>:{" "}
                  <span className="text-slate-500">{st.status}</span>
                </span>
              ))}
            </div>
          ) : null}

          {/* Bucket tabs */}
          <div
            className="mt-8 flex flex-wrap gap-2"
            data-testid="jobs-bucket-tabs"
            role="tablist"
          >
            {BUCKETS.map((b) => {
              const count = buckets[b.key]?.length || 0;
              const isActive = b.key === activeBucket;
              return (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => setActiveBucket(b.key)}
                  className={
                    "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all " +
                    (isActive
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-300")
                  }
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`bucket-tab-${b.key}`}
                >
                  <b.icon className="h-3.5 w-3.5" />
                  {b.label}
                  <span
                    className={
                      "inline-flex items-center justify-center min-w-[1.25rem] rounded-full px-1.5 text-[10px] font-semibold " +
                      (isActive ? "bg-white text-slate-900" : "bg-slate-100 text-slate-600")
                    }
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Body: filters + bucket panel */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[18rem_1fr] gap-6">
            <JobFilters
              filters={filters}
              onChange={setFilters}
              onClear={onClearFilters}
              resultsCount={filteredJobs.length}
            />

            <section data-testid={`bucket-panel-${active.key}`}>
              <header className="flex items-center gap-3 mb-4">
                <span
                  className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${active.iconClass}`}
                >
                  <active.icon className="h-4 w-4" />
                </span>
                <div>
                  <h2 className="text-lg font-medium text-slate-900">{active.label}</h2>
                  <p className="text-xs text-slate-500">{active.blurb}</p>
                </div>
              </header>

              {activeJobs.length === 0 ? (
                <div
                  className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center"
                  data-testid={`bucket-empty-${active.key}`}
                >
                  <p className="text-sm text-slate-500">
                    {matches.length === 0
                      ? "No jobs returned yet. Connect Adzuna keys or enable demo mode to populate this view."
                      : "No matches in this bucket with your current filters."}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {activeJobs.map((job) => {
                    const k = keyFor(job);
                    const row = busyRows[k] || {};
                    return (
                      <JobCard
                        key={k}
                        job={job}
                        onSave={onSave}
                        onMarkApplied={onMarkApplied}
                        isSaved={savedKeys.has(k)}
                        isApplied={appliedKeys.has(k)}
                        savingState={row.save}
                        applyingState={row.apply}
                      />
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
